import React, { useState, useEffect } from 'react';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Switch } from './ui/switch';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from './ui/dialog';
import { Table, TableHeader, TableBody, TableRow, TableHead, TableCell } from './ui/table';
import { Spinner } from './ui/spinner';
import { useToast } from './ui/toast';
import { Plus, X } from 'lucide-react';
import api from '../services/api';

const Districts = () => {
  const [districts, setDistricts] = useState([]);
  const [loading, setLoading] = useState(false);
  const [modalOpen, setModalOpen] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [form, setForm] = useState({ name: '', khoroos: [], isActive: true });
  const [khorooInput, setKhorooInput] = useState('');
  const [deleteConfirm, setDeleteConfirm] = useState(null);
  const { toast } = useToast();

  const fetchDistricts = async () => {
    setLoading(true);
    try { const res = await api.get('/districts?all=true'); setDistricts(res.data); }
    catch { toast({ title: 'Дүүргүүд татахад алдаа гарлаа', variant: 'error' }); }
    setLoading(false);
  };

  useEffect(() => { fetchDistricts(); }, []);

  const handleAddKhoroo = () => {
    const num = parseInt(khorooInput);
    if (!num || num < 1) return;
    if (form.khoroos.includes(num)) {
      toast({ title: `${num}-р хороо аль хэдийн нэмэгдсэн`, variant: 'error' });
      return;
    }
    setForm(f => ({ ...f, khoroos: [...f.khoroos, num].sort((a, b) => a - b) }));
    setKhorooInput('');
  };

  const handleAddKhorooRange = () => {
    const num = parseInt(khorooInput);
    if (!num || num < 1) return;
    const range = [];
    for (let i = 1; i <= num; i++) {
      if (!form.khoroos.includes(i)) range.push(i);
    }
    setForm(f => ({ ...f, khoroos: [...f.khoroos, ...range].sort((a, b) => a - b) }));
    setKhorooInput('');
  };

  const handleRemoveKhoroo = (num) => {
    setForm(f => ({ ...f, khoroos: f.khoroos.filter(k => k !== num) }));
  };

  const handleSubmit = async () => {
    try {
      if (editingId) {
        await api.put(`/districts/${editingId}`, form);
        toast({ title: 'Дүүрэг шинэчлэгдлээ', variant: 'success' });
      } else {
        await api.post('/districts', form);
        toast({ title: 'Дүүрэг нэмэгдлээ', variant: 'success' });
      }
      setModalOpen(false);
      setForm({ name: '', khoroos: [], isActive: true });
      setEditingId(null);
      fetchDistricts();
    } catch (error) {
      toast({ title: error.response?.data?.message || 'Алдаа гарлаа', variant: 'error' });
    }
  };

  const handleDelete = async (id) => {
    try {
      await api.delete(`/districts/${id}`);
      toast({ title: 'Устгагдлаа', variant: 'success' });
      setDeleteConfirm(null);
      fetchDistricts();
    } catch {
      toast({ title: 'Устгахад алдаа гарлаа', variant: 'error' });
    }
  };

  return (
    <div>
      <div className="flex items-center justify-between mb-4">
        <h2 className="text-xl font-bold">Дүүргүүд</h2>
        <Button onClick={() => { setEditingId(null); setForm({ name: '', khoroos: [], isActive: true }); setModalOpen(true); }}>
          <Plus className="h-4 w-4 mr-2" />Дүүрэг нэмэх
        </Button>
      </div>

      {loading ? (
        <div className="flex justify-center py-20"><Spinner /></div>
      ) : (
        <div className="border rounded-lg overflow-x-auto bg-white">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Нэр</TableHead>
                <TableHead>Хороод</TableHead>
                <TableHead>Идэвхтэй</TableHead>
                <TableHead>Үйлдэл</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {districts.map(d => (
                <TableRow key={d._id}>
                  <TableCell className="font-medium">{d.name}</TableCell>
                  <TableCell>
                    {d.khoroos?.length > 0 ? (
                      <span className="text-sm text-zinc-600">
                        {d.khoroos.length} хороо ({d.khoroos[0]}-{d.khoroos[d.khoroos.length - 1]})
                      </span>
                    ) : (
                      <span className="text-sm text-zinc-400">-</span>
                    )}
                  </TableCell>
                  <TableCell>{d.isActive ? 'Тийм' : 'Үгүй'}</TableCell>
                  <TableCell>
                    <div className="flex gap-1">
                      <Button size="sm" variant="outline" onClick={() => {
                        setEditingId(d._id);
                        setForm({ name: d.name, khoroos: d.khoroos || [], isActive: d.isActive });
                        setModalOpen(true);
                      }}>
                        Засах
                      </Button>
                      <Button size="sm" variant="destructive" onClick={() => setDeleteConfirm(d._id)}>Устгах</Button>
                    </div>
                  </TableCell>
                </TableRow>
              ))}
              {districts.length === 0 && (
                <TableRow><TableCell colSpan={4} className="text-center py-8 text-zinc-400">Дүүрэг байхгүй</TableCell></TableRow>
              )}
            </TableBody>
          </Table>
        </div>
      )}

      <Dialog open={modalOpen} onOpenChange={(v) => { if (!v) { setModalOpen(false); setEditingId(null); } }}>
        <DialogContent>
          <DialogHeader><DialogTitle>{editingId ? 'Дүүрэг засах' : 'Дүүрэг нэмэх'}</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <div><label className="text-sm font-medium">Нэр *</label>
              <Input value={form.name} onChange={e => setForm(f => ({ ...f, name: e.target.value }))} /></div>

            <div>
              <label className="text-sm font-medium">Хороод</label>
              <div className="flex gap-2 mt-1">
                <Input type="number" min="1" placeholder="Хорооны дугаар"
                  value={khorooInput} onChange={e => setKhorooInput(e.target.value)}
                  onKeyDown={e => e.key === 'Enter' && handleAddKhoroo()} />
                <Button size="sm" variant="outline" onClick={handleAddKhoroo}>Нэмэх</Button>
                <Button size="sm" variant="outline" onClick={handleAddKhorooRange}
                  title="1-ээс оруулсан тоо хүртэл бүгдийг нэмнэ">1-{khorooInput || 'N'}</Button>
              </div>
              {form.khoroos.length > 0 && (
                <div className="flex flex-wrap gap-1 mt-2">
                  {form.khoroos.map(k => (
                    <span key={k} className="inline-flex items-center gap-1 px-2 py-1 bg-primary/10 text-primary rounded-md text-sm">
                      {k}-р хороо
                      <button onClick={() => handleRemoveKhoroo(k)} className="hover:text-red-600">
                        <X className="h-3 w-3" />
                      </button>
                    </span>
                  ))}
                </div>
              )}
            </div>

            <div className="flex items-center gap-2">
              <Switch checked={form.isActive} onChange={v => setForm(f => ({ ...f, isActive: v }))} />
              <label className="text-sm">Идэвхтэй</label>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => { setModalOpen(false); setEditingId(null); }}>Болих</Button>
            <Button onClick={handleSubmit}>{editingId ? 'Хадгалах' : 'Нэмэх'}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={!!deleteConfirm} onOpenChange={(v) => !v && setDeleteConfirm(null)}>
        <DialogContent>
          <DialogHeader><DialogTitle>Устгах уу?</DialogTitle></DialogHeader>
          <p className="text-sm text-zinc-600">Энэ дүүргийг устгахдаа итгэлтэй байна уу?</p>
          <DialogFooter>
            <Button variant="outline" onClick={() => setDeleteConfirm(null)}>Болих</Button>
            <Button variant="destructive" onClick={() => handleDelete(deleteConfirm)}>Устгах</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default Districts;
